import type { CircuitEdge, CircuitEdgeColor } from '../types/track.js';
import { EDGE_STRIPE_CSS, EDGE_STRIPE_HEX, forEachStyledEdgePart } from './circuitSurfaceStyle.js';

export interface CircuitEdgeDrawTarget {
  fillRect(x: number, y: number, w: number, h: number, color: number | string): void;
}

function drawThickSegment(
  target: CircuitEdgeDrawTarget,
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  thickness: number,
  color: number | string
): void {
  const dx = x2 - x1;
  const dy = y2 - y1;
  if (Math.abs(dx) >= Math.abs(dy)) {
    target.fillRect(x1, y1 - thickness / 2, x2 - x1, thickness, color);
  } else {
    target.fillRect(x1 - thickness / 2, y1, thickness, y2 - y1, color);
  }
}

export function drawCircuitCellEdges(
  target: CircuitEdgeDrawTarget,
  px: number,
  py: number,
  cellSize: number,
  edges: Partial<Record<CircuitEdge, CircuitEdgeColor>>,
  options?: { pad?: number; thickness?: number; curbSegment?: number; useHex?: boolean }
): void {
  const pad = options?.pad ?? Math.max(2, cellSize * 0.05);
  const thickness = options?.thickness ?? Math.max(3, cellSize * 0.14);
  const curbSegment = options?.curbSegment ?? Math.max(4, cellSize * 0.18);
  const palette = options?.useHex ? EDGE_STRIPE_HEX : EDGE_STRIPE_CSS;

  const drawEdge = (edge: CircuitEdge, x1: number, y1: number, x2: number, y2: number) => {
    const style = edges[edge];
    if (!style) return;
    forEachStyledEdgePart(x1, y1, x2, y2, style, curbSegment, (sx, sy, ex, ey, stripe) => {
      drawThickSegment(target, sx, sy, ex, ey, thickness, palette[stripe]);
    });
  };

  drawEdge('north', px + pad, py + pad, px + cellSize - pad, py + pad);
  drawEdge('south', px + pad, py + cellSize - pad, px + cellSize - pad, py + cellSize - pad);
  drawEdge('west', px + pad, py + pad, px + pad, py + cellSize - pad);
  drawEdge('east', px + cellSize - pad, py + pad, px + cellSize - pad, py + cellSize - pad);
}
